import React, { createContext, useContext, useReducer } from "react";
import { workOrderReducer } from "./reducer";

import { API, graphqlOperation } from "aws-amplify";
import { aisleByActiveByZoneBySortKey } from "../../../graphql/reloadyard/custom-queries";
import {
  getWorkOrderMobile,
  workOrderByStateReduced,
} from "../../../graphql/workOrder/queriesLite";
import { performUpdateWorkOrder } from "../../../graphql/workOrder/workOrderCRUDLite";
import {
  adaptWorkOrderForCrewDetail,
  adaptWorkOrderForCrewList,
} from "../../../graphql/workOrder/workOrderAdaptersLite";
import { performUpdateInventoryMini } from "../../../graphql/inventory/inventoryCRUD";

import { useNotify } from "ra-core";

import {
  filterAisles,
  setInventoryAisleList,
} from "../../../utils/locationUtils";
import { logError } from "../../../utils/appUtils";
import { updateExceptionQuantity } from "../../../utils/utils";
import { UserType, WorkOrderState } from "../../../utils/constants";

const initState = {
  placedWorkOrders: [],
  currentInventory: {},
  currentWorkOrder: {},
  mode: "LIST_WORK_ORDERS",
  errors: {},
  notes: {},
  selectedAisle: null,
  selectedLocation: null,
  selectedInventoryItem: { quantityOnHand: 1 },
  inventoryItemsMap: {},
  isContainerNumberValidated: false,
  showCapacityDisplay: false,
  bayList: [],
  displayState: "INVENTORY_ITEMS",
  disableSelection: false,
  searchInventoryItemsMap: {},
  showInventoryQuantity: false,
  loading: false,
};

const WorkOrderContext = createContext(initState);

export const WorkOrderProvider = React.memo(({ children }) => {
  const [state, dispatch] = useReducer(workOrderReducer, initState);
  const notify = useNotify();

  const getAisles = async (currentInventory) => {
    try {
      const zoneId = currentInventory?.zoneId?.trim();
      if (!zoneId) {
        dispatch({ type: "SET_AISLES", payload: [] });
        return [];
      }
      const aislesResponse = await API.graphql(
        graphqlOperation(aisleByActiveByZoneBySortKey, {
          zoneId,
          activeSortKey: { beginsWith: { active: "true" } },
          limit: 300,
        }),
      );
      const aisles = filterAisles(
        aislesResponse.data.aisleByActiveByZoneBySortKey.items,
        currentInventory,
      );
      dispatch({ type: "SET_AISLES", payload: aisles });
      return aisles;
    } catch (err) {
      logError(
        "processWorkOrders:WorkOrderAisleMap:getAisles",
        err,
        notify,
      );
      return [];
    }
  };

  const saveInventory = async (inventory, quantityReceived) => {
    inventory.quantityReceived = quantityReceived;
    setInventoryAisleList(inventory);
    updateExceptionQuantity(inventory);
    const toUpdateInventory = { ...inventory };
    toUpdateInventory.productId = toUpdateInventory.productId || "";

    return performUpdateInventoryMini(toUpdateInventory);
  };

  const getWorkOrders = async () => {
    const fetchByState = async (state) =>
      API.graphql(
      graphqlOperation(workOrderByStateReduced, {
        reloadYardId: window.appUser?.reloadYardId,
        state: { eq: state },
        limit: 100,
      }),
    );

    const listStates = [WorkOrderState.READY, WorkOrderState.IN_PROGRESS];
    const listResponses = await Promise.all(listStates.map(fetchByState));
    let data = listResponses.flatMap((response) =>
      (response?.data?.workOrderByState?.items || []).map(adaptWorkOrderForCrewList),
    );

    if (window.appUser.type !== UserType.CREW) {
      const reviewResponse = await fetchByState(WorkOrderState.PENDING_REVIEW);
      const reviewRows = (reviewResponse?.data?.workOrderByState?.items || []).map(
        adaptWorkOrderForCrewList,
      );
      if (reviewRows.length > 0) {
        data = [
          ...data,
          ...reviewRows,
        ];
      }
    }

    // Exclude SIT-linked work orders from crew queue.
    data = data.filter(
      (workOrder) => !workOrder.sit,
    );
    data.sort((a, b) => a.placedDateTime.localeCompare(b.placedDateTime));
    return data;
  };

  const getWorkOrderById = async (id) => {
    const response = await API.graphql(
      graphqlOperation(getWorkOrderMobile, { id }),
    );
    return adaptWorkOrderForCrewDetail(response?.data?.getWorkOrder);
  };

  const getWorkOrder = async (match) => getWorkOrderById(match.params.id);

  const saveWorkOrder = async (toUpdateWorkOrder) => {
    const workOrderToUpdate = { ...toUpdateWorkOrder };

    const response = await performUpdateWorkOrder(workOrderToUpdate);
    const updatedWorkOrderId =
      response?.data?.updateWorkOrder?.id || workOrderToUpdate?.id;
    if (!updatedWorkOrderId) {
      throw new Error("saveWorkOrder: missing work order id after update");
    }

    // Re-fetch canonical detail so partial updates (e.g. {id, history}) do not
    // overwrite in-memory state with a reduced object.
    const refreshedWorkOrder = await API.graphql(
      graphqlOperation(getWorkOrderMobile, { id: updatedWorkOrderId }),
    );
    const payload = adaptWorkOrderForCrewDetail(
      refreshedWorkOrder?.data?.getWorkOrder,
    );
    dispatch({
      type: "UPDATE_CURRENT_SHIPMENT",
      payload,
    });
    return payload;
  };

  return (
    <WorkOrderContext.Provider
      value={{
        state,
        dispatch,
        getWorkOrders,
        getWorkOrder,
        getAisles,
        saveInventory,
        saveWorkOrder,
        // TODO(work-order-migration): remove these `*Shipment` aliases once
        // every crew/work-order file has been switched to the work-order
        // names. Tracked alongside the inbound → work-order port; safe to
        // delete when no consumers reference them via grep.
        getShipments: getWorkOrders,
        getShipment: getWorkOrder,
        saveShipment: saveWorkOrder,
        notify,
      }}
    >
      {children}
    </WorkOrderContext.Provider>
  );
});

export const useWorkOrderContext = () => useContext(WorkOrderContext);
// TODO(work-order-migration): remove these legacy-name re-exports once all
// imports use the work-order names.
export const useInboundContext = useWorkOrderContext;
export const InboundProvider = WorkOrderProvider;
